<template>
  <v-app>
    <v-navigation-drawer rail permanent :border="false" color="secondary">
      <v-list density="compact" nav>
        <v-tooltip v-for="item in visibleNavigationItems" :key="item.value">
          {{ item.label }}
          <template v-slot:activator="{ props }">
            <v-list-item
              class="nav-element"
              v-bind="props"
              :value="item.value"
              :active="selectedItem === item.value"
              active-class="active-nav-element"
              @click="selectNavigationItem(item)"
            >
              <v-icon :icon="item.icon" />
            </v-list-item>
          </template>
        </v-tooltip>
      </v-list>

      <template v-slot:append>
        <v-list density="compact" nav>
          <v-tooltip>
            {{ $t('LAYOUTS.LOGOUT') }}
            <template v-slot:activator="{ props }">
              <v-list-item class="nav-element" v-bind="props" @click="logout()" value="exit">
                <v-icon icon="mdi-exit-to-app" />
              </v-list-item>
            </template>
          </v-tooltip>
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-app-bar v-if="!hideHeader">
      <img alt="logo Boomerang" class="logo" src=@/assets/images/boomerang_black_logo.png>

      <v-spacer />

      <v-tooltip location="bottom">
        {{ $t('LAYOUTS.PROFILE') }}
        <template v-slot:activator="{ props }">
          <v-btn-icon
            class="nav-btn"
            v-bind="props"
            icon="mdi-account-outline"
            @click="selectProfile"
            :color="profileActive ? 'primary' : 'black'"
            variant="tonal"
          />
        </template>
      </v-tooltip>

      <button
        ref="selectedCollaborator"
        class="selected-collaborator"
        type="button"
        @click="showCollaborators = !showCollaborators"
        :disabled="hideCollaboratorMenu"
      >
        <span class="d-flex flex-column align-start">
          <span class="selected-collaborator-name">
            {{ currentCollaborator.firstname + ' ' + currentCollaborator.lastname }}
          </span>
          <span class="selected-collaborator-role">{{ getAccountRole }}</span>
        </span>
        <v-icon
          v-if="!hideCollaboratorMenu"
          :icon="showCollaborators ? 'mdi-chevron-down' : 'mdi-chevron-up'"
        />
      </button>

      <div class="collaborators-list" v-if="showCollaborators" :style="{ minWidth: minWidth }">
        <ul>
          <li
            v-for="collaborator of getCollaboratorList"
            :key="collaborator.technical_id"
            :class="{ active: getActiveCollaborator(collaborator.technical_id) }"
            @click="selectCollaborator(collaborator)"
          >
            {{ collaborator.firstname + ' ' + collaborator.lastname }}
            <v-icon v-if="getActiveCollaborator(collaborator.technical_id)" icon="mdi-check" />
          </li>
        </ul>
      </div>
    </v-app-bar>

    <v-main class="main">
      <slot />
    </v-main>

    <RightNavigationDrawer
      :currentRepairFile="repairDrawerStore.payload.currentRepairFile"
      :rentalFileId="repairDrawerStore.payload.rentalFileId"
      :refreshCurrentRepairFile="repairDrawerStore.payload.refreshCurrentRepairFile"
      :newActionComponentProps="repairDrawerStore.isOpen"
      :openDeliveryClientDrawer="repairDrawerStore.openDeliveryClientDrawer"
      @updateCurrentRepairFile="handleRepairDrawerUpdateCurrentRepairFile()"
      @showUpdateRepairFileStatusModal="handleRepairDrawerShowUpdateStatusModal()"
      @close="handleRepairDrawerClose()"
      @showGuaranteeExtensionBoomerangModal="handleRepairDrawerShowGuaranteeExtensionModal()"
      @openRentalWarningModal="handleRepairDrawerOpenRentalWarningModal()"
      @reset-delivery-client-state="repairDrawerStore.resetDeliveryClientState()"
    />
  </v-app>
</template>

<script>
import { handleResponseError } from '@/utils/toastUtils'
import { useAuthStore } from '@/store/authStore'
import { useRepairDrawerStore } from '@/store/repairDrawerStore'
import { checkPermission } from '@/utils/permissionUtils'
import CollaboratorService from '@/services/CollaboratorService'
import RightNavigationDrawer from '@/components/repair-files/right-drawer/RightNavigationDrawer.vue'
import {
  resetPrimarySecondaryThemeColors,
  setPrimarySecondaryThemeColors
} from '@/plugins/vuetify/config'

export default {
  name: 'AdminLayout',
  components: {
    RightNavigationDrawer
  },
  data() {
    const authStore = useAuthStore()
    const repairDrawerStore = useRepairDrawerStore()
    return {
      authStore,
      currentCollaborator: authStore.getCurrentCollaborator,
      repairDrawerStore,
      selectedItem: 'repairFiles',
      profileActive: false,
      showCollaborators: false,
      minWidth: '0px',
      navigationItems: [
        {
          value: 'repairFiles',
          label: this.$t('LAYOUTS.REPAIR_FILE'),
          icon: 'mdi-tag-outline',
          routeName: 'repairFiles',
          permissions: ['ADMIN_VIEW_REPAIR_FILE']
        },
        {
          value: 'accounts',
          label: this.$t('LAYOUTS.ACCOUNTS'),
          icon: 'mdi-account-multiple',
          routeName: 'accounts',
          permissions: ['ADMIN_ACCOUNT_READ_PERMISSION', 'ADMIN_ROLE_READ_PERMISSION']
        },
        {
          value: 'networks',
          label: this.$t('LAYOUTS.DISTRIBUTOR_NETWORK'),
          icon: 'mdi-source-branch',
          routeName: 'distributorNetworks',
          permissions: ['ADMIN_DISTRIBUTOR_NETWORK_READ_PERMISSION']
        },
        {
          value: 'manufacturers',
          label: this.$t('LAYOUTS.MANUFACTURERS'),
          icon: 'mdi-hammer-screwdriver',
          routeName: 'manufacturers',
          permissions: ['ADMIN_PRODUCT_READ_PERMISSION']
        },
        {
          value: 'key-account-networks',
          label: this.$t('LAYOUTS.KEY_ACCOUNT_NETWORK'),
          icon: 'mdi-crane',
          routeName: 'keyAccountNetworks',
          permissions: ['ADMIN_KEY_ACCOUNT_NETWORK_READ_PERMISSION']
        },
        {
          value: 'key-account-products',
          label: this.$t('LAYOUTS.KEY_ACCOUNT_PRODUCT'),
          icon: 'mdi-tools',
          routeName: 'keyAccountProducts',
          permissions: ['ADMIN_KEY_ACCOUNT_PRODUCT_READ_PERMISSION']
        },
        {
          value: 'repairers',
          label: this.$t('LAYOUTS.REPAIRMAN'),
          icon: 'mdi-wrench',
          routeName: 'repairers',
          permissions: ['ADMIN_REPAIRER_READ_PERMISSION']
        },
        {
          value: 'transporters',
          label: this.$t('LAYOUTS.TRANSPORTER'),
          icon: 'mdi-truck-fast-outline',
          routeName: 'transporters',
          permissions: ['ADMIN_TRANSPORTER_READ_PERMISSION']
        },
        {
          value: 'import-export',
          label: this.$t('LAYOUTS.IMPORTS_EXPORTS'),
          icon: 'mdi-swap-horizontal',
          routeName: 'importExport',
          permissions: [
            'ADMIN_EXTRACT_PRODUCT_REFERENTIAL',
            'ADMIN_IMPORT_PRODUCT_REFERENTIAL',
            'ADMIN_EXTRACT_PACKAGE_REFERENTIAL',
            'ADMIN_IMPORT_PACKAGE_REFERENTIAL',
            'ADMIN_EXTRACT_SERIAL_NUMBER_REFERENTIAL',
            'ADMIN_IMPORT_SERIAL_NUMBER_REFERENTIAL',
            'ADMIN_EXTRACT_GUARANTEE_EXTENSION_REFERENTIAL',
            'ADMIN_IMPORT_GUARANTEE_EXTENSION_REFERENTIAL',
            'ADMIN_EXTRACT_GUARANTEE_REFERENTIAL',
            'ADMIN_IMPORT_GUARANTEE_REFERENTIAL',
            'ADMIN_EXTRACT_PRODUCT_BREAKDOWN_REFERENTIAL',
            'ADMIN_IMPORT_PRODUCT_BREAKDOWN_REFERENTIAL',
            'ADMIN_EXTRACT_PRODUCT_DISTRIBUTOR_REF_REFERENTIAL',
            'ADMIN_IMPORT_PRODUCT_DISTRIBUTOR_REF_REFERENTIAL',
            'ADMIN_EXTRACT_PRODUCT_STORE_PRICE_REFERENTIAL',
            'ADMIN_IMPORT_PRODUCT_STORE_PRICE_REFERENTIAL',
            'ADMIN_EXTRACT_TRANSPORT_PACKAGE_REFERENTIAL',
            'ADMIN_IMPORT_TRANSPORT_PACKAGE_REFERENTIAL',
            'ADMIN_EXTRACT_TRANSPORTER_REFERENTIAL',
            'ADMIN_IMPORT_TRANSPORTER_REFERENTIAL',
            'ADMIN_EXTRACT_ESTIMATE_TRANSPORTER_COST_REFERENTIAL',
            'ADMIN_IMPORT_ESTIMATE_TRANSPORTER_COST_REFERENTIAL',
            'ADMIN_EXTRACT_ADDITIONAL_REPAIR_COST_REFERENTIAL',
            'ADMIN_IMPORT_ADDITIONAL_REPAIR_COST_REFERENTIAL',
            'ADMIN_EXTRACT_RECYCLER_BAC_REFERENTIAL',
            'ADMIN_IMPORT_RECYCLER_BAC_REFERENTIAL',
            'ADMIN_EXTRACT_RECYCLING_BUYBACK_REFERENTIAL',
            'ADMIN_IMPORT_RECYCLING_BUYBACK_REFERENTIAL',
            'ADMIN_EXTRACT_RECYCLING_CLIENT_LINK_REFERENTIAL',
            'ADMIN_IMPORT_RECYCLING_CLIENT_LINK_REFERENTIAL',
            'ADMIN_EXTRACT_RENTAL_PRODUCT_REFERENTIAL',
            'ADMIN_IMPORT_RENTAL_PRODUCT_REFERENTIAL',
            'ADMIN_EXTRACT_KEY_ACCOUNT_PRODUCT_REFERENTIAL',
            'ADMIN_KEY_ACCOUNT_PRODUCT_IMPORT_PERMISSION'
          ]
        },
        {
          value: 'email-outbox',
          label: this.$t('LAYOUTS.EMAIL_OUTBOX'),
          icon: 'mdi-email-sync-outline',
          routeName: 'emailOutbox',
          permissions: ['ADMIN_EMAIL_OUTBOX_READ']
        },
        {
          value: 'others',
          label: this.$t('LAYOUTS.OTHER_MANAGEMENT'),
          icon: 'mdi-cog-outline',
          routeName: 'others',
          permissions: [
            'ADMIN_CREATE_NOTIFICATION',
            'ADMIN_ITECH_MANUAL_UPDATE',
            'ADMIN_CHRONOPOST_MANUAL_UPDATE',
            'ADMIN_DISTRIBUTOR_NETWORK_READ_PERMISSION'
          ]
        },
        {
          value: 'rental',
          label: this.$t('LAYOUTS.RENTAL_MODULE'),
          icon: 'mdi-boomerang',
          routeName: 'rentalProducts',
          permissions: ['ADMIN_RENTAL_READ']
        },
        {
          value: 'recycling',
          label: this.$t('LAYOUTS.RECYCLING_MODULE'),
          icon: 'mdi-recycle',
          routeName: 'recyclingModule',
          permissions: ['ADMIN_RECYCLING_READ']
        }
      ]
    }
  },
  watch: {
    $route: {
      handler: function (params) {
        this.selectedItem = this.$route.meta.menuSelected
        this.profileActive = params.fullPath.startsWith('/profile')
      },
      deep: true,
      immediate: true
    },
    showCollaborators(bool) {
      if (bool) {
        this.minWidth = this.$refs.selectedCollaborator.clientWidth + 1 + 'px'
      }
    },
    currentNetwork: {
      handler(network) {
        this.applyNetworkTheme(network)
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    currentNetwork() {
      return this.authStore?.userDetail?.network || null
    },
    visibleNavigationItems() {
      return this.navigationItems.filter((item) => this.checkPermission(item.permissions))
    },
    getCollaboratorList() {
      return this.authStore.getCollaboratorList
    },
    getAccountRole() {
      const role = this.authStore.getAccountRole
      if (role) {
        return role.title
      } else {
        return ''
      }
    },
    hideCollaboratorMenu() {
      return this.getCollaboratorList.length <= 1
    },
    hideHeader() {
      return this.$route.meta.hideHeader === true
    }
  },
  methods: {
    checkPermission,
    handleRepairDrawerClose() {
      this.repairDrawerStore.closeDrawer()
    },
    handleRepairDrawerUpdateCurrentRepairFile() {
      const callback = this.repairDrawerStore.payload.onUpdateCurrentRepairFile
      if (typeof callback === 'function') {
        callback()
      }
    },
    handleRepairDrawerShowUpdateStatusModal() {
      const callback = this.repairDrawerStore.payload.onShowUpdateRepairFileStatusModal
      if (typeof callback === 'function') {
        callback()
      }
    },
    handleRepairDrawerShowGuaranteeExtensionModal() {
      const callback = this.repairDrawerStore.payload.onShowGuaranteeExtensionBoomerangModal
      if (typeof callback === 'function') {
        callback()
      }
    },
    handleRepairDrawerOpenRentalWarningModal() {
      const callback = this.repairDrawerStore.payload.onOpenRentalWarningModal
      if (typeof callback === 'function') {
        callback()
      }
    },
    async logout() {
      this.loading = true

      try {
        await this.authStore.logoutAction()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        await this.$router.push({ name: 'login' })
      }
    },
    selectNavigationItem(item) {
      this.selectedItem = item.value
      this.$router.push({ name: item.routeName })
    },
    selectProfile() {
      this.$router.push({ name: 'profile' })
    },
    async selectCollaborator(collaborator) {
      try {
        await CollaboratorService.switchCollaborator(collaborator.technical_id)

        this.showCollaborators = false
        this.currentCollaborator = collaborator
        this.authStore.selectedCollaborator(this.currentCollaborator.technical_id)
      } catch (error) {
        handleResponseError(error)
      }
    },
    getActiveCollaborator(collaboratorId) {
      return this.currentCollaborator.technical_id === parseInt(collaboratorId)
    },
    applyNetworkTheme(network) {
      if (
        network?.custom_theme_enabled &&
        typeof network?.primary_color === 'string' &&
        typeof network?.secondary_color === 'string'
      ) {
        setPrimarySecondaryThemeColors(network.primary_color, network.secondary_color)
        return
      }

      resetPrimarySecondaryThemeColors()
    }
  }
}
</script>

<style>
/* remove scroll bar on left navigation bar */
.v-navigation-drawer__content::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px #5d5d5d;
}

.v-navigation-drawer__content::-webkit-scrollbar {
  width: 0;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb {
  -webkit-box-shadow: inset 0 0 6px #424242;
}

/* remove scroll bar on left navigation bar */
.logo {
  align-items: center;
  justify-content: center;
  max-width: 600px;
  max-height: 100px;
}

.main {
  background-color: var(--default-background-color);
}

.nav-element {
  border-radius: var(--default-radius);
  overflow: visible;
}

.active-nav-element {
  background-color: rgb(var(--v-theme-surface));
}

.active-nav-element .v-icon {
  color: rgb(var(--v-theme-secondary));
}

.grecaptcha-badge {
  visibility: hidden !important;
}

.selected-collaborator {
  height: 100%;
  display: flex;
  min-width: 250px;
  flex-direction: row;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  justify-content: space-between;
}

.selected-collaborator:hover {
  cursor: pointer;
}

.selected-collaborator:disabled {
  cursor: default;
}

.selected-collaborator-name {
  color: rgb(var(--v-theme-secondary));
  font-weight: bold;
}

.selected-collaborator-role {
  font-size: 13px;
  color: #616161;
}

.collaborators-list {
  right: 0;
  top: 64px;
  z-index: 1010;
  position: fixed;
  width: fit-content;
}

.collaborators-list ul li {
  list-style: none;
  padding: 10px 20px;
  color: rgb(var(--v-theme-secondary));
  font-weight: bold;
  font-size: 14px;
  display: flex;
  align-items: center;
  background-color: white;
  border-top: 1px solid var(--vuetify-grey);
  border-left: 1px solid var(--vuetify-grey);
  border-right: 1px solid var(--vuetify-grey);
  border-bottom: unset;
  justify-content: space-between;
}

.collaborators-list ul li:last-child {
  border-bottom: 1px solid var(--vuetify-grey);
}

.collaborators-list ul li:hover {
  background-color: var(--default-background-color);
  cursor: pointer;
}

.collaborators-list ul li.active {
  background-color: #f8f8f8;
  cursor: pointer;
}

.collaborators-list ul li.active:hover {
  background-color: var(--default-background-color);
}
</style>
